import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import type ReactQuill from 'react-quill-new';

interface UseCustomQuillEditorProps {
  value?: string;
  onChange: (content: string) => void;
  placeholder: string;
}

const stripTags = (html: string) => html.replace(/<[^>]*>/g, '').trim();

export const useCustomQuillEditor = ({ 
  value, 
  onChange, 
  placeholder 
}: UseCustomQuillEditorProps) => {
  const [localValue, setLocalValue] = useState(value || '');
  const quillRef = useRef<ReactQuill>(null);

  useEffect(() => {
    if (value === undefined) return;
    
    // Only update local state if the incoming meaningful text is different.
    // This prevents cursor jumping when the parent echoes the exact same content back.
    const incomingText = stripTags(value);
    const localText = stripTags(localValue);

    if (incomingText !== localText) {
      setLocalValue(value);
    }
  }, [value, localValue]);

  useEffect(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor || !editor.root) return;

    const handlePaste = (e: ClipboardEvent) => {
      const text = e.clipboardData?.getData('text/plain');
      if (!text) return;

      // Intercept and prevent Quill's default paste parser which drops or wipes external HTML
      e.preventDefault();

      const normalizedText = text.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
      const range = editor.getSelection(true);
      const index = range ? range.index : Math.max(0, editor.getLength() - 1);
      const length = range ? range.length : 0;

      if (length > 0) {
        editor.deleteText(index, length, 'user');
      }

      editor.insertText(index, normalizedText, 'user');
      editor.setSelection(index + normalizedText.length, 0, 'silent');
    };

    const root = editor.root;
    root.addEventListener('paste', handlePaste);
    return () => {
      root.removeEventListener('paste', handlePaste);
    };
  }, []);

  const handleChange = useCallback((content: string) => {
    setLocalValue(content);
    onChange(content);
  }, [onChange]);

  const cleanPlaceholder = useMemo(() => {
    return placeholder ? placeholder.replace(/<[^>]*>?/gm, '').trim() : '';
  }, [placeholder]);

  return {
    quillRef,
    localValue,
    cleanPlaceholder,
    handleChange
  };
};